import { ArrayKeyValueObject, IDocumentHelpView, KeyValueObject } from "@/UI/Interfaces/IBaseUI";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import produce from "immer";
import { ComponentType, LazyExoticComponent } from "react";

export enum EHelpType {
  SideHelp = 1,
  DocumentHelp = 2,
  ShortHelp = 3,
  DimensionHelp = 4,
  Normal = 5
}

export enum EHelpOpenFor {
  Grid = 1,
  Field = 2,
  Document = 3,
}

export enum EHelpOpenIn {
  EntryForm = 1,
  DataAnalytics = 2,
  ReportExplorer = 3,
  Dashboard = 4,
}


interface IHelpConfig {
  helpObject: string;
  helpobjectno: string;
  singlecolumn: string;
  multiplereturn: boolean;
  multiplecolumn: string;
  serverpaging: boolean;
}

export interface IHelpHeader {
  ColumnText: string;
  ColumnID: string;
  controltype: string;
  fielddataformatno: string;
  ColumnWidth: number;
  ishidden: boolean;
  hyperlinktransactioncode: string;
}

interface IHelpConfigObject {
  Config: IHelpConfig;
  Header: Array<IHelpHeader>;
  Detail: ArrayKeyValueObject;
  RecordsTotal: number;
  RecordsFiltered: number;
}

export interface IReturnParam {
  fieldId: string,
  uniqueKey?: string,
  gridno?: string,
  gridRowIndex?: number,
}

export interface IReturnValues {
  fieldId: string;
  fieldValue: string;
  dimensionValues?: KeyValueObject<string>;

}

interface IShortHelpOptions {
  top: number,
  left: number,
  isOpen: boolean,
  searchText: string
}
interface IHelp {
  [tabKey: string]: {
    loadedHelp?: {
      helpConfig: IHelpConfigObject;
      helpObjectNo: string;
      helpWhere: string;
      helpTags: KeyValueObject;
      helpType: EHelpType;
      helpOpenFor: EHelpOpenFor;
      multiSelect: boolean;
      helpOpenIn: EHelpOpenIn;
      returnParam: IReturnParam;
      shortHelpOptions?: IShortHelpOptions,
      helpView: Omit<IDocumentHelpView["setting"], "filterApproval" | "frozenColumns" | "columnIndex" | "columnWidth" | "columnHidden">,
      forceOpen: boolean;
      // returnValues?: Array<IReturnValues> | Array<Array<IReturnValues>> // for Grid;
      // isOpen: boolean;
      isSubGrid: boolean;
      isDropDown: boolean;
    },
    returnOption?: {
      returnParam: IReturnParam;
      selectedRow: any[];
      returnValues?: IReturnValues[][]
    }

  };
}

export interface IGetHelp {
  helpObject: string;
  helpWhere: string;
  returnParams: IReturnParam;
  multiSelect?: boolean;
  isSubGrid?: boolean
  isDropDown?: boolean;
  helpType?: EHelpType;
  helpOpenIn?: EHelpOpenIn;
  dimensionValues?: KeyValueObject;
  isAccountDimension?: boolean;
  shortHelpOptions?: IShortHelpOptions,
}

const InitialState: IHelp = {};

const HelpSlice = createSlice({
  name: "help",
  initialState: InitialState,
  reducers: {

    getHelpConfig(state, action: PayloadAction<IGetHelp>) { },
    loadHelpData: {
      reducer: (state, action: PayloadAction<{ objectId: string, filter: Partial<Exclude<IDocumentHelpView["setting"], "frozenColumns" | "columnIndex" | "columnWidth" | "columnHidden">> }, string, { helpLoaded: () => void } | undefined>) => { },
      prepare: (payload: { objectId: string, filter: Partial<Exclude<IDocumentHelpView["setting"], "frozenColumns" | "columnIndex" | "columnWidth" | "columnHidden">> }, meta?: { helpLoaded: () => void } | undefined) => ({
        payload,
        meta,
      })
    },

    setHelpConfig(state, action: PayloadAction<{ config: IHelpConfigObject; issetting: boolean; helpObjectNo: string; helpWhere: string; helpTags: KeyValueObject; tabKey: string, returnParams: IReturnParam, helpType: EHelpType, helpOpenFor: EHelpOpenFor, multiSelect: boolean, helpOpenIn: EHelpOpenIn, isSubGrid: boolean, isDropDown?: boolean, shortHelpOptions?: IShortHelpOptions }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {

        const dateConvertColumn = payload.config.Header.filter(x => x.controltype.isEqual("DTE", "TME"));

        const data = payload.config.Detail.map((x: any) => {
          dateConvertColumn.map(y => {
            x[y.ColumnID] = new Date(x[y.ColumnID]);
          });
          return x;
        });

        draftState[payload.tabKey] = {
          loadedHelp: {
            helpConfig: {
              ...payload.config,
              Detail: data
            },
            helpObjectNo: payload.helpObjectNo,
            helpWhere: payload.helpWhere,
            helpTags: payload.helpTags,
            forceOpen: payload.issetting,
            helpType: payload.helpType,
            helpOpenFor: payload.helpOpenFor,
            multiSelect: payload.multiSelect,
            helpOpenIn: payload.helpOpenIn,
            returnParam: payload.returnParams,
            helpView: {
              filterCondition: [],
              orderBy: [],
              pageIndex: -1,
              pageSize: 25,
              wholeSearchParameter: [],
              wholeSearchValue: ""
            },
            // isOpen: true,
            isSubGrid: payload.isSubGrid,
            isDropDown: payload.isDropDown || false,
            shortHelpOptions: payload.shortHelpOptions || undefined,
          }
        };
      });
    },

    setHelpDetailData(state, action: PayloadAction<{ tabKey: string, data: KeyValueObject }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        const help = draftState[payload.tabKey].loadedHelp;
        if (help) {

          const dateConvertColumn = help.helpConfig.Header.filter(x => x.controltype.isEqual("DTE", "TME"));

          const data = payload.data.data.map((x: any) => {
            dateConvertColumn.map(y => {
              x[y.ColumnID] = new Date(x[y.ColumnID]);
            });
            return x;
          });

          help.helpConfig.Detail = data;
          help.helpConfig.RecordsTotal = payload.data.recordsTotal;
          help.helpConfig.RecordsFiltered = payload.data.recordsFiltered;
        }
      });
    },

    setHelpView(state, action: PayloadAction<{ tabKey: string, view: Omit<IDocumentHelpView["setting"], "filterApproval" | "frozenColumns" | "columnIndex" | "columnWidth" | "columnHidden"> }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        const help = draftState[payload.tabKey].loadedHelp;
        if (help) {
          help.helpView = payload.view;
        }
      });
    },

    setHelpReturnValue(state, action: PayloadAction<{ objectId: string, selectedRow: any[], returnValue: IReturnValues[][], returnParam: IReturnParam }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        if (!draftState[payload.objectId])
          draftState[payload.objectId] = {};

        draftState[payload.objectId].returnOption = {
          returnParam: payload.returnParam,
          selectedRow: payload.selectedRow,
          returnValues: payload.returnValue
        }

      });
    },
    closeHelp(state, action: PayloadAction<{ objectId: string }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        if (!draftState[payload.objectId]?.loadedHelp?.forceOpen)
          delete draftState[payload.objectId];
      });
    },

    forceOpenHelp(state, action: PayloadAction<{ objectId: string }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        let loadedHelp = draftState[payload.objectId]?.loadedHelp;
        if (loadedHelp)
          loadedHelp.forceOpen = true;
      });
    },
    setSearchText(state, action: PayloadAction<{ objectId: string, searchText: string }>) {
      const payload = action.payload;

      return produce(state, (draftState) => {
        const activeHelp = draftState[payload.objectId]?.loadedHelp;

        if (activeHelp && activeHelp.shortHelpOptions) {
          activeHelp.shortHelpOptions.searchText = payload.searchText;
        }
      });
    }
  }
});

export const { getHelpConfig, loadHelpData, setHelpConfig, setHelpDetailData, setHelpView, setHelpReturnValue, closeHelp, forceOpenHelp, setSearchText } = HelpSlice.actions;

export default HelpSlice.reducer;
